import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SectionCard, LoadingState, StatusBadge, Pill, ActionButton, EmptyState, ClientWorkspaceLink } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_TONES, usd, fmtDate, PAYMENT_METHOD_OPTIONS, PAYMENT_METHOD_LABELS, ACCEPTANCE_METHOD_OPTIONS, LineItemEditor } from "./common";
import { RecordAcceptanceDialog, RecordPaymentDialog } from "./ReconcileDialogs";

// Server-driven action presentation. Buttons render ONLY when the action is present
// in the server-returned `available_actions` array (RBAC + lifecycle + business rules).
const ACTION_META = {
  edit: { label: "Edit", icon: "Pencil", variant: "secondary" },
  submit_review: { label: "Submit for Review", icon: "SearchCheck", variant: "secondary" },
  approve: { label: "Approve", icon: "ShieldCheck", variant: "secondary" },
  send: { label: "Send to Client", icon: "Send", variant: "primary" },
  record_acceptance: { label: "Record Acceptance", icon: "CheckCircle2", variant: "primary" },
  accept: { label: "Mark Accepted", icon: "CheckCircle", variant: "primary" },
  decline: { label: "Mark Declined", icon: "XCircle", variant: "secondary" },
  record_external_payment: { label: "Record Payment", icon: "Banknote", variant: "primary" },
  create_invoice: { label: "Create Invoice", icon: "FileText", variant: "primary" },
  view_invoice: { label: "View Invoice", icon: "ArrowRight", variant: "secondary" },
  prepare_engagement_letter: { label: "Prepare Engagement Letter", icon: "FileSignature", variant: "primary" },
  resend_engagement_letter: { label: "Resend Engagement Letter", icon: "RefreshCw", variant: "secondary" },
  duplicate: { label: "Duplicate", icon: "Copy", variant: "secondary" },
  void: { label: "Void", icon: "Ban", variant: "danger" },
  delete_draft: { label: "Delete Draft", icon: "Trash2", variant: "danger" },
};
const ACTION_ORDER = [
  "edit", "submit_review", "approve", "send", "accept", "record_acceptance",
  "record_external_payment", "create_invoice", "view_invoice",
  "prepare_engagement_letter", "resend_engagement_letter",
  "duplicate", "decline", "void", "delete_draft",
];

export default function EstimateWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState([]);
  const [revisionReason, setRevisionReason] = useState("");
  const [dialog, setDialog] = useState(null); // 'acceptance' | 'payment' | 'void' | 'accept' | 'create_invoice'
  const [engSendCtx, setEngSendCtx] = useState(null); // { mode: "send"|"resend", engagementId, clientName }
  const [timeline, setTimeline] = useState(null); // {events, portal_activity_summary}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sales/estimates/${id}`);
      setEst(data);
      // Fetch timeline in parallel (non-blocking — failure is silenced)
      api.get(`/sales/estimates/${id}/timeline`)
        .then(({ data: tl }) => setTimeline(tl))
        .catch(() => {});
    } catch (e) {
      toast.error("Could not load estimate"); navigate("/sales/estimates");
    } finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const actions = (est?.available_actions || []).map((a) => a.action);
  const has = (a) => actions.includes(a);

  // Find the view_invoice action metadata (contains invoice_id)
  const viewInvoiceAction = (est?.available_actions || []).find((a) => a.action === "view_invoice");

  const run = async (action, fn, msg) => {
    setBusy(action);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(""); }
  };

  // Notify ClientWorkspace (same tab or cross-tab) that a lifecycle change occurred.
  const dispatchLifecycleUpdate = (clientId) => {
    if (!clientId) return;
    const key = `tf.ws.lifecycle-update.${clientId}`;
    localStorage.setItem(key, Date.now().toString());
    window.dispatchEvent(new CustomEvent("client:lifecycle-updated", { detail: { clientId } }));
  };

  const handlePrepareEngagement = async () => {
    // STEP 1 — Prepare (only when no engagement exists yet)
    // Step 2 (sending) is a separate staff-confirmed action inside EngagementSendDialog.
    const resendAction = (est?.available_actions || []).find((a) => a.action === "resend_engagement_letter");
    if (resendAction?.engagement_id) {
      // Engagement already exists, unsigned — open resend dialog directly.
      setEngSendCtx({ mode: "resend", engagementId: resendAction.engagement_id,
                      clientName: est?.client?.full_name || "the client" });
      return;
    }
    // No engagement yet — prepare first, then open send dialog using returned ID.
    if (busy === "prepare_engagement_letter") return; // guard duplicate submit
    setBusy("prepare_engagement_letter");
    try {
      const { data } = await api.post(`/sales/estimates/${id}/prepare-engagement`);
      const engId = data.engagement_id;
      if (!engId) throw new Error("No engagement ID returned from preparation");
      await load(); // refresh available_actions before opening dialog
      setEngSendCtx({ mode: "send", engagementId: engId,
                      clientName: est?.client?.full_name || "the client" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Engagement preparation failed");
    } finally { setBusy(""); }
  };

  const handle = (action) => {
    switch (action) {
      case "edit": setItems((est.line_items || []).map((li) => ({ ...li }))); setRevisionReason(""); setEditing(true); break;
      case "submit_review": run(action, () => api.post(`/sales/estimates/${id}/submit-review`), "Submitted for review"); break;
      case "approve": run(action, () => api.post(`/sales/estimates/${id}/approve`), "Approved"); break;
      case "send": run(action, () => api.post(`/sales/estimates/${id}/send`, { base_url: window.location.origin }), "Estimate sent"); break;
      case "decline": run(action, () => api.post(`/sales/estimates/${id}/decline`), "Declined"); break;
      case "duplicate": run(action, async () => { const { data } = await api.post(`/sales/estimates/${id}/duplicate`); navigate(`/sales/estimates/${data.id}`); }, "Duplicated"); break;
      case "delete_draft":
        if (window.confirm("Delete this draft estimate? This cannot be undone.")) run(action, async () => { await api.delete(`/sales/estimates/${id}`); navigate("/sales/estimates"); }, "Draft deleted");
        break;
      case "accept": setDialog("accept"); break;
      case "record_acceptance": setDialog("acceptance"); break;
      case "record_external_payment": setDialog("payment"); break;
      case "void": setDialog("void"); break;
      case "create_invoice": setDialog("create_invoice"); break;
      case "prepare_engagement_letter": handlePrepareEngagement(); break;
      case "resend_engagement_letter": handlePrepareEngagement(); break;
      case "view_invoice":
        if (viewInvoiceAction?.invoice_id) navigate(`/finance/invoices/${viewInvoiceAction.invoice_id}`);
        else if (est?.active_invoice?.id) navigate(`/finance/invoices/${est.active_invoice.id}`);
        break;
      default: break;
    }
  };

  const saveEdit = async () => {
    setBusy("edit");
    try {
      await api.patch(`/sales/estimates/${id}`, { line_items: items.filter((i) => i.name), revision_reason: revisionReason || null });
      toast.success("Estimate updated"); setEditing(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(""); }
  };

  if (loading) return <LoadingState label="Loading estimate…" />;
  if (!est) return null;

  const payments = est.payments || [];
  const total = est.total || 0;
  const paid = est.total_paid || 0;
  const remaining = est.remaining_balance != null ? est.remaining_balance : total - paid;
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  const isConverted = est.status === "converted_to_invoice" || est.status === "converted";

  return (
    <div data-testid="estimate-workspace">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/sales/estimates")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="estimate-back"><Icons.ArrowLeft className="h-4 w-4" /></button>
          {est.client_id && (
            <button onClick={() => navigate(`/client-workspace/${est.client_id}`)}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-700 border border-slate-200 rounded-lg px-2.5 py-1.5 transition-colors"
              data-testid="estimate-open-workspace">
              <Icons.User className="h-3.5 w-3.5" />Client Workspace
            </button>
          )}
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl font-semibold text-slate-900" data-testid="estimate-number">{est.estimate_number}</h1>
              <StatusBadge status={est.status} map={STATUS_TONES} />
              {isConverted && <Pill tone="emerald"><Icons.FileCheck className="h-3 w-3 mr-1 inline" />Invoice Created</Pill>}
              {est.is_historical_acceptance && <Pill tone="neutral"><Icons.History className="h-3 w-3 mr-1 inline" />Historical</Pill>}
              {est.locked && <Pill tone="neutral"><Icons.Lock className="h-3 w-3 mr-1 inline" />Locked</Pill>}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <ClientWorkspaceLink clientId={est.client_id} clientName={est.client?.full_name} clientNumber={est.client?.client_number} testId="estimate-client-link" />
              {est.title && <><span className="text-slate-300">·</span><span className="text-sm text-slate-500">{est.title}</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="estimate-actions">
          {ACTION_ORDER.filter(has).map((a) => {
            const m = ACTION_META[a];
            return <ActionButton key={a} variant={m.variant} icon={m.icon} loading={busy === a} onClick={() => handle(a)} data-testid={`action-${a}`}>{m.label}</ActionButton>;
          })}
          {actions.length === 0 && <span className="text-xs text-slate-400">No actions available in this state.</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <SectionCard title="Line Items" icon="Receipt">
            {editing ? (
              <div className="space-y-3" data-testid="estimate-edit-panel">
                <LineItemEditor items={items} setItems={setItems} services={[]} addons={[]} currency={est.currency} />
                <div>
                  <label className="text-xs text-slate-500">Revision reason (optional)</label>
                  <input value={revisionReason} onChange={(e) => setRevisionReason(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="edit-revision-reason" placeholder="Why is this changing?" />
                </div>
                <div className="flex justify-end gap-2">
                  <ActionButton variant="secondary" onClick={() => setEditing(false)}>Cancel</ActionButton>
                  <ActionButton loading={busy === "edit"} onClick={saveEdit} data-testid="edit-save">Save Changes</ActionButton>
                </div>
              </div>
            ) : (
              <>
                <div className="border border-slate-100 rounded-lg divide-y divide-slate-50">
                  {(est.line_items || []).map((li) => (
                    <div key={li.id} className="flex justify-between px-3 py-2.5 text-sm">
                      <span className="text-slate-700">{li.name} <span className="text-slate-400">×{li.quantity}</span></span>
                      <span className="font-medium">{usd(li.amount, est.currency)}</span>
                    </div>))}
                </div>
                <div className="flex justify-end text-base font-semibold text-slate-900 mt-3">Total: {usd(est.total, est.currency)}</div>
                {est.payment_terms && <p className="text-xs text-slate-400 mt-2 italic">{est.payment_terms}</p>}
              </>
            )}
          </SectionCard>

          {/* Invoice Summary — shows when estimate is accepted, payment_pending, paid, or converted_to_invoice */}
          {(est.status === "accepted" || est.status === "payment_pending" || est.status === "paid" || isConverted) && (
            <InvoiceSummaryCard est={est} onCreateInvoice={() => setDialog("create_invoice")} onViewInvoice={() => handle("view_invoice")} />
          )}

          {(est.status === "payment_pending" || est.status === "paid" || payments.length > 0) && (
            <SectionCard title="Payments" icon="Banknote" data-testid="estimate-payments">
              <div className="grid grid-cols-3 gap-3 mb-3">
                <Stat label="Total" value={usd(total, est.currency)} />
                <Stat label="Paid" value={usd(paid, est.payment_currency || est.currency)} tone="emerald" />
                <Stat label="Remaining" value={usd(remaining, est.payment_currency || est.currency)} tone={remaining > 0 ? "amber" : "slate"} />
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-4"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
              {payments.length === 0 ? <p className="text-sm text-slate-400">No payments recorded yet.</p> : (
                <div className="border border-slate-100 rounded-lg divide-y divide-slate-50">
                  {payments.map((p) => (
                    <div key={p.id} className="px-3 py-2.5 text-sm" data-testid={`payment-row-${p.id}`}>
                      <div className="flex justify-between">
                        <span className="font-medium text-slate-800">{usd(p.amount, p.currency)}</span>
                        <span className="text-slate-500">{PAYMENT_METHOD_LABELS[p.method] || p.method}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                        <span>Paid {fmtDate(p.paid_on)}</span>
                        {p.evidence_reference && <span className="inline-flex items-center gap-1"><Icons.Paperclip className="h-3 w-3" />{p.evidence_reference}</span>}
                        <span>by {p.recorded_by_name}</span>
                      </div>
                      {p.reason && <div className="text-xs text-slate-500 mt-0.5 italic">"{p.reason}"</div>}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}
        </div>

        <div className="space-y-5">
          <SectionCard title="Details" icon="Info">
            <Detail label="Client" value={est.client?.full_name} />
            <Detail label="Status" value={<StatusBadge status={est.status} map={STATUS_TONES} />} />
            <Detail label="Currency" value={est.currency} />
            <Detail label="Expires" value={fmtDate(est.expiration_date)} />
            <Detail label="Sent" value={fmtDate(est.sent_at)} />
          </SectionCard>

          {est.decision_trace && (
            <SectionCard title="Estimate Decision Trace" icon="GitBranch" data-testid="estimate-decision-trace">
              <div className="text-xs text-slate-500 mb-2">{est.decision_trace.engine} · <span className="font-medium text-slate-700">{est.decision_trace.decision}</span></div>
              <ul className="space-y-1.5">
                {(est.decision_trace.reasoning || []).map((r, i) => (
                  <li key={i} className="flex gap-2 text-xs text-slate-600"><Icons.ChevronRight className="h-3 w-3 mt-0.5 text-slate-400 shrink-0" />{r}</li>
                ))}
              </ul>
              {(est.decision_trace.evidence || []).length > 0 && (
                <div className="mt-3 pt-2 border-t border-slate-50">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Evidence considered</div>
                  {(est.decision_trace.evidence || []).map((e, i) => <div key={i} className="text-xs text-slate-500">• {e}</div>)}
                </div>
              )}
            </SectionCard>
          )}

          {/* Portal Activity — enriched: shows viewed, downloads, questions, last activity */}
          {(est.viewed_at || est.portal_accepted_by_client_id || est.accepted_at) && (
            <SectionCard title="Portal Activity" icon="Eye" data-testid="estimate-portal-activity">
              {est.viewed_at && (
                <Detail label="Viewed" value={fmtDate(est.viewed_at)} />
              )}
              {(() => {
                const pa = timeline?.portal_activity_summary;
                if (!pa) return null;
                return (<>
                  {pa.pdf_download_count > 0 && (
                    <Detail
                      label="PDF Downloaded"
                      value={`${fmtDate(pa.last_pdf_downloaded_at)}${pa.pdf_download_count > 1 ? ` (${pa.pdf_download_count}×)` : ""}`}
                    />
                  )}
                  {pa.question_count > 0 && (
                    <Detail label="Questions Asked" value={`${pa.question_count}`} />
                  )}
                  {pa.accepted_at && (
                    <Detail label="Accepted" value={fmtDate(pa.accepted_at)} />
                  )}
                  {pa.declined_at && (
                    <Detail label="Declined" value={fmtDate(pa.declined_at)} />
                  )}
                  {pa.last_portal_activity_at && (
                    <Detail label="Last Activity" value={fmtDate(pa.last_portal_activity_at)} />
                  )}
                </>);
              })()}
            </SectionCard>
          )}

          {(est.status === "accepted" || est.status === "payment_pending" || est.status === "paid" || isConverted || est.accepted_on) && (
            <SectionCard title="Acceptance" icon="CheckCircle2" data-testid="estimate-acceptance">
              <Detail label="Accepted on" value={fmtDate(est.accepted_on || est.accepted_at)} />
              <Detail label="Method" value={est.acceptance_method ? <span className="capitalize">{String(est.acceptance_method).replace(/_/g, " ")}</span> : "—"} />
              <Detail label="Accepted by" value={est.accepted_by} />
              {est.is_historical_acceptance && <Detail label="Recorded by" value={`${est.recorded_by_name || "—"} · ${fmtDate(est.recorded_at)}`} />}
              {est.acceptance_reason && <div className="pt-2 text-xs text-slate-500 italic">"{est.acceptance_reason}"</div>}
              {est.acceptance_evidence_reference && <div className="pt-1 text-xs text-slate-500 inline-flex items-center gap-1"><Icons.Paperclip className="h-3 w-3" />{est.acceptance_evidence_reference}</div>}
              {isConverted && (
                <div className="mt-2 pt-2 border-t border-slate-50">
                  <Detail label="Invoice created" value={fmtDate(est.converted_to_invoice_at)} />
                  {est.invoice_number && <Detail label="Invoice #" value={<span className="font-mono text-xs">{est.invoice_number}</span>} />}
                </div>
              )}
            </SectionCard>
          )}

          {est.status === "voided" && (
            <SectionCard title="Voided" icon="Ban" data-testid="estimate-voided">
              <Detail label="Voided on" value={fmtDate(est.voided_at)} />
              <Detail label="By" value={est.voided_by_name} />
              {est.void_reason && <div className="pt-2 text-xs text-slate-500 italic">"{est.void_reason}"</div>}
            </SectionCard>
          )}
        </div>
      </div>

      {/* Commercial Timeline — full estimate lifecycle below the main grid */}
      {timeline?.events?.length > 0 && (
        <div className="mt-6" data-testid="estimate-commercial-timeline">
          <SectionCard title="Commercial Timeline" icon="GitCommit">
            <ol className="relative border-l border-slate-200 ml-2 space-y-0">
              {timeline.events.map((ev, i) => {
                const isPortal = ev.actor_role === "portal";
                const isAuto   = ev.event_type?.includes("followup");
                return (
                  <li key={i} className="mb-0 ml-5 pb-5 last:pb-0">
                    <span className={`absolute -left-2 flex items-center justify-center w-4 h-4 rounded-full ring-2 ring-white
                      ${isPortal ? "bg-blue-500" : isAuto ? "bg-amber-400" : "bg-slate-400"}`} />
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`text-xs font-semibold ${isPortal ? "text-blue-700" : isAuto ? "text-amber-700" : "text-slate-700"}`}>
                        {ev.title}
                      </span>
                      {ev.actor_name && (
                        <span className="text-[10px] text-slate-400">
                          by {ev.actor_name}
                        </span>
                      )}
                    </div>
                    <time className="block text-[10px] text-slate-400 mt-0.5">
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleString(undefined, {dateStyle:"medium",timeStyle:"short"}) : "—"}
                    </time>
                    {ev.description && (
                      <p className="mt-0.5 text-xs text-slate-500 leading-tight">{ev.description}</p>
                    )}
                  </li>
                );
              })}
            </ol>
          </SectionCard>
        </div>
      )}

      <AcceptDialog open={dialog === "accept"} onClose={() => setDialog(null)} est={est}
        onDone={() => { dispatchLifecycleUpdate(est?.client_id); load(); }} />
      <RecordAcceptanceDialog open={dialog === "acceptance"} onClose={() => setDialog(null)} estimateId={est.id} clientName={est.client?.full_name}
        onDone={() => { dispatchLifecycleUpdate(est?.client_id); load(); }} />
      <RecordPaymentDialog open={dialog === "payment"} onClose={() => setDialog(null)} estimateId={est.id} currency={est.currency} remaining={remaining} onDone={load} />
      <VoidDialog open={dialog === "void"} onClose={() => setDialog(null)} est={est} onDone={load} />
      <CreateInvoiceDialog
        open={dialog === "create_invoice"}
        onClose={() => setDialog(null)}
        est={est}
        onDone={(invoiceId) => { setDialog(null); navigate(`/finance/invoices/${invoiceId}`); }}
      />
      <EngagementSendDialog
        open={!!engSendCtx}
        ctx={engSendCtx}
        onClose={() => setEngSendCtx(null)}
        onDone={() => { setEngSendCtx(null); load(); }}
      />
    </div>
  );
}

// ── Invoice Summary Card ────────────────────────────────────────────────────
function InvoiceSummaryCard({ est, onCreateInvoice, onViewInvoice }) {
  const inv = est.active_invoice;
  const isConverted = est.status === "converted_to_invoice" || est.status === "converted";
  const hasHistoricalPaid = (est.total_paid || 0) > 0;

  const INV_STATUS_TONE = {
    draft: "bg-slate-100 text-slate-600",
    issued: "bg-blue-50 text-blue-700",
    sent: "bg-blue-50 text-blue-700",
    awaiting_payment: "bg-amber-50 text-amber-700",
    partially_paid: "bg-orange-50 text-orange-700",
    paid: "bg-emerald-50 text-emerald-700",
    overdue: "bg-rose-50 text-rose-700",
    void: "bg-slate-100 text-slate-400",
    voided: "bg-slate-100 text-slate-400",
    overpayment: "bg-purple-50 text-purple-700",
  };

  return (
    <SectionCard title="Invoice" icon="FileText" data-testid="estimate-invoice-summary">
      {!inv && !isConverted ? (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">No invoice created yet.</p>
            {hasHistoricalPaid && (
              <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                <Icons.Info className="h-3 w-3" />
                Historical payment of {usd(est.total_paid, est.currency)} recorded — will carry forward when invoice is created.
              </p>
            )}
          </div>
          <ActionButton variant="primary" icon="FilePlus" onClick={onCreateInvoice} data-testid="invoice-summary-create-btn">
            Create Invoice
          </ActionButton>
        </div>
      ) : inv ? (
        <div className="space-y-3">
          {/* Header row: invoice number + status + View button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-medium text-slate-800" data-testid="invoice-summary-number">{inv.invoice_number}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${INV_STATUS_TONE[inv.status] || "bg-slate-100"}`} data-testid="invoice-summary-status">
                {(inv.status || "").replace(/_/g, " ")}
              </span>
            </div>
            <button onClick={onViewInvoice} data-testid="invoice-summary-view-btn"
              className="text-sm text-[#F5A623] hover:text-[#e0951a] font-medium flex items-center gap-1">
              View Invoice <Icons.ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Financial summary */}
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Amount" value={usd(inv.total, inv.currency)} />
            <Stat label="Paid" value={usd(inv.amount_paid, inv.currency)} tone="emerald" />
            <Stat label="Balance" value={usd(inv.balance, inv.currency)} tone={(inv.balance || 0) > 0 ? "amber" : "slate"} />
          </div>

          {/* Due date */}
          {inv.due_date && (
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <Icons.Calendar className="h-3 w-3" />
              Due {fmtDate(inv.due_date)}
            </div>
          )}

          {/* Overpayment alert */}
          {inv.status === "overpayment" && (
            <div className="text-xs text-purple-700 bg-purple-50 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="invoice-overpayment-alert">
              <Icons.AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Overpayment detected — paid {usd(inv.amount_paid, inv.currency)} exceeds invoice total {usd(inv.total, inv.currency)}. Staff review required.</span>
            </div>
          )}

          {/* Currency reconciliation required */}
          {inv.carry_forward_status === "pending_currency_reconciliation" && (
            <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="invoice-currency-reconciliation-alert">
              <Icons.AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                <strong>Historical payment carry-forward blocked</strong>
                <p className="mt-0.5">One or more historical payments have a different currency from the invoice. Staff must manually reconcile the payment before it can be recorded.</p>
                {(inv.carry_forward_blocked || []).map((b, i) => (
                  <p key={i} className="mt-0.5 font-mono text-[10px]">
                    Payment {b.amount} {b.payment_currency} → Invoice is {b.invoice_currency}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Most recent payment provenance */}
          {inv.most_recent_payment && (
            <div className="text-xs text-slate-400 pt-1 border-t border-slate-50 flex items-center gap-1" data-testid="invoice-last-payment">
              <Icons.ArrowDownLeft className="h-3 w-3 text-emerald-500" />
              <span>
                Last payment: {usd(inv.most_recent_payment.amount, inv.most_recent_payment.currency)}
                {inv.most_recent_payment.carry_forward_type === "historical_estimate_reconciliation" && (
                  <span className="ml-1 text-amber-600">(carried from estimate)</span>
                )}
                {inv.most_recent_payment.received_date && ` · ${fmtDate(inv.most_recent_payment.received_date)}`}
              </span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Invoice information unavailable.</p>
      )}
    </SectionCard>
  );
}

// ── Create Invoice Dialog ───────────────────────────────────────────────────
function CreateInvoiceDialog({ open, onClose, est, onDone }) {
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setDueDate(""); setError(null); } }, [open]);

  const historicalPaid = est?.total_paid || 0;
  const historicalPayments = est?.payments || [];
  const total = est?.total || 0;
  const balanceToInvoice = Math.max(0, total - historicalPaid);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const { data: inv } = await api.post(`/finance/invoices/from-estimate/${est.id}`, dueDate ? { due_date: dueDate } : {});
      toast.success(`Invoice ${inv.invoice_number} created`);
      onDone(inv.id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error_code === "invoice_already_exists") {
        setError(`Invoice ${detail.invoice_number || ""} already exists for this estimate.`);
      } else {
        setError(formatApiErrorDetail(detail) || "Could not create invoice");
      }
    } finally { setBusy(false); }
  };

  if (!est) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Invoice</DialogTitle>
          <DialogDescription>Review the invoice details before confirming. Line items, prices, and currency are inherited from the accepted estimate and cannot be changed here.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Client + Estimate */}
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Client" value={est.client?.full_name || "—"} />
            <FieldRow label="Source Estimate" value={est.estimate_number} mono />
          </div>

          {/* Line items (read-only) */}
          <div>
            <div className="text-xs text-slate-500 mb-1.5">Line Items</div>
            <div className="border border-slate-100 rounded-lg divide-y divide-slate-50 bg-slate-50/50">
              {(est.line_items || []).map((li) => (
                <div key={li.id} className="flex justify-between px-3 py-2 text-sm">
                  <span className="text-slate-700">{li.name} <span className="text-slate-400">×{li.quantity || 1}</span></span>
                  <span className="font-medium text-slate-800">{usd(li.amount, est.currency)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Historical payments — show details when present */}
          {historicalPayments.length > 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
                <Icons.History className="h-3 w-3" />
                Historical Payments — will carry forward to invoice
              </div>
              <div className="border border-amber-100 rounded-lg divide-y divide-amber-50 bg-amber-50/30">
                {historicalPayments.map((p) => (
                  <div key={p.id} className="flex justify-between px-3 py-2 text-xs">
                    <span className="text-slate-600">{p.reason || p.method}</span>
                    <span className="font-medium text-amber-700">{usd(p.amount, p.currency || est.currency)}</span>
                  </div>
                ))}
                <div className="flex justify-between px-3 py-2 text-xs font-semibold bg-amber-50">
                  <span className="text-slate-600">Total historical payments</span>
                  <span className="text-amber-700">{usd(historicalPaid, est.currency)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Invoice Amount" value={`${est.currency} ${Number(total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} highlight />
            {historicalPaid > 0 && <FieldRow label="Balance to Invoice" value={usd(balanceToInvoice, est.currency)} highlight />}
          </div>

          {/* Payment terms (read-only) */}
          {est.payment_terms && (
            <FieldRow label="Payment Terms" value={est.payment_terms} />
          )}

          {/* Due date (editable) */}
          <div>
            <label className="text-xs text-slate-500 block mb-1">Due Date <span className="text-slate-400">(optional — can be set later)</span></label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="create-invoice-due-date"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
          </div>

          {error && (
            <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2" data-testid="create-invoice-error">{error}</div>
          )}

          {historicalPaid > 0 && (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 flex items-start gap-2">
              <Icons.CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Historical payments will be automatically carried forward into the invoice upon creation. No re-entry required.</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <ActionButton variant="secondary" onClick={onClose} data-testid="create-invoice-cancel">Cancel</ActionButton>
          <ActionButton loading={busy} onClick={submit} data-testid="create-invoice-confirm">Create Invoice</ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, value, mono = false, highlight = false }) {
  return (
    <div>
      <div className="text-xs text-slate-400 mb-0.5">{label}</div>
      <div className={`text-sm ${highlight ? "font-semibold text-slate-900" : "text-slate-700"} ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-800", emerald: "text-emerald-700", amber: "text-amber-700" };
  return <div className="rounded-lg border border-slate-100 p-2.5"><div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div><div className={`text-sm font-semibold ${tones[tone]}`}>{value}</div></div>;
}
function Detail({ label, value }) {
  return <div className="flex justify-between gap-4 py-2 border-b border-slate-50 last:border-0"><span className="text-sm text-slate-500">{label}</span><span className="text-sm font-medium text-slate-800 text-right">{value || "—"}</span></div>;
}

function AcceptDialog({ open, onClose, est, onDone }) {
  const [agree, setAgree] = useState(false);
  const [acceptedBy, setAcceptedBy] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAgree(false); setAcceptedBy(est?.client?.full_name || ""); } }, [open, est]);
  const submit = async () => {
    if (!agree) return toast.error("Confirm agreement to the estimated charges");
    setBusy(true);
    try { await api.post(`/sales/estimates/${est.id}/accept`, { agree: true, acceptance_method: "staff_assisted", accepted_by: acceptedBy }); toast.success("Estimate accepted"); onClose(); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="accept-dialog">
        <DialogHeader><DialogTitle className="font-heading">Mark Accepted</DialogTitle>
          <DialogDescription>Record the client's acceptance of this estimate.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-slate-500">Accepted by</label><input value={acceptedBy} onChange={(e) => setAcceptedBy(e.target.value)} data-testid="accept-by" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
          <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="accept-agree" /><span className="font-medium">I AGREE TO THE ESTIMATED SERVICES AND CHARGES.</span></label>
        </div>
        <DialogFooter className="gap-2"><ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton><ActionButton loading={busy} onClick={submit} data-testid="accept-confirm">Confirm</ActionButton></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoidDialog({ open, onClose, est, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const hasInvoice = !!est?.active_invoice;
  const submit = async () => {
    if (!reason.trim()) return toast.error("A reason is required to void");
    setBusy(true);
    try { await api.post(`/sales/estimates/${est.id}/void`, { reason: reason.trim() }); toast.success("Estimate voided"); onClose(); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="void-dialog">
        <DialogHeader><DialogTitle className="font-heading">Void Estimate</DialogTitle>
          <DialogDescription>Voiding permanently retires this estimate. This is recorded in the audit log.</DialogDescription></DialogHeader>
        {hasInvoice && (
          <div className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2.5 flex items-start gap-2">
            <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>This estimate has a linked invoice (<span className="font-mono text-xs">{est.active_invoice.invoice_number}</span>). Voiding the estimate does <strong>not</strong> void the invoice. Manage the invoice separately.</span>
          </div>
        )}
        <div><label className="text-xs text-slate-500">Reason <span className="text-rose-500">*</span></label><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="void-reason" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Why is this estimate being voided?" /></div>
        <DialogFooter className="gap-2"><ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton><ActionButton variant="danger" loading={busy} onClick={submit} data-testid="void-confirm">Void Estimate</ActionButton></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * EngagementSendDialog
 * Two modes:
 *   "send"   — engagement just prepared; staff confirms before first dispatch.
 *   "resend" — unsigned engagement already exists; staff confirms a re-dispatch.
 *
 * Safeguards:
 *   - Confirm button disabled while request is in flight (guards duplicate clicks).
 *   - Send failure shown inline; dialog stays open for retry.
 *   - Dialog does not close or imply success until POST succeeds.
 */
function EngagementSendDialog({ open, ctx, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Reset error whenever dialog is opened with new context
  useEffect(() => { if (open) setSendError(null); }, [open, ctx?.engagementId]);

  if (!ctx) return null;
  const isResend = ctx.mode === "resend";
  const title = isResend ? "Resend Engagement Letter" : "Send for Signature";
  const confirmLabel = isResend ? "Resend" : "Send for Signature";
  const description = isResend
    ? `Resend the engagement letter to ${ctx.clientName} for e-signature. A new link will be generated.`
    : `Send the prepared engagement letter to ${ctx.clientName} for e-signature.`;

  const submit = async () => {
    if (busy) return; // guard duplicate click
    setBusy(true);
    setSendError(null);
    try {
      await api.post(`/engagements/${ctx.engagementId}/send`,
        { base_url: window.location.origin });
      toast.success(isResend ? "Engagement letter resent" : "Engagement letter sent for signature");
      onDone(); // caller reloads available_actions; this also closes the dialog
    } catch (e) {
      // Keep dialog open — show error inline for retry
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Failed to send — please try again";
      setSendError(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent data-testid="engagement-send-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {sendError && (
          <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 rounded-lg px-3 py-2.5"
            data-testid="engagement-send-error">
            <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{sendError}</span>
          </div>
        )}

        <DialogFooter className="gap-2">
          <ActionButton variant="secondary" onClick={onClose} disabled={busy}
            data-testid="engagement-send-cancel">
            {sendError ? "Close" : "Not Yet"}
          </ActionButton>
          <ActionButton
            loading={busy}
            disabled={busy}
            onClick={submit}
            data-testid="engagement-send-confirm">
            {confirmLabel}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
